import { Alert, Row, Col, Card, Button, message, Modal, Collapse, Space, Tag, Typography } from "antd";
import {AppleFilled, GoogleOutlined, MailOutlined, LoadingOutlined, DownloadOutlined, CopyOutlined} from '@ant-design/icons';
import {
  formatDate,
  isSupportLead,
  isSupportManager,
  isSupportAgent3,
  isDev,
  formatLicenseVersion
} from "../../../helpers/genericHelper";
import LoaderComponent from "../../Loader";
import CustomText from "../../CustomText";
import DuplicateUsers from "./DuplicateUsers";
import { useParams } from "react-router";
import { useEffect, useState } from "react";
import API from "../../../services/API";
import UpdateAccountModal from "./UpdateAccountModal";
import AdminChanges from "./AdminChanges";
import RegistrationDetails from "./RegistrationDetails";
import BlockHistory from "./BlockHistory";
import SillaDocuments from "./SillaDocuments";
import { GOOGLE_PROVIDER, EMAIL_PROVIDER, APPLE_PROVIDER } from "../../../constants/StringConstants";
import Data from "../../CustomText/data";
import ProfileActions from "./Profile/Actions";
import ProfileInfo from "./Profile/Info";
import SSNMatch from "./Profile/SSNMatch";
import PhoneNumberDisplay from "./Profile/PhoneNumberDisplay";
import AuditProfile from "./AuditProfile";
import DocumentVerificationLink from "./DocumentVerificationLink";
import DocumentVerifications from "./DocumentVerifications";
import HighRiskRules from "./HighRiskRules";
import AllowlistedRules from "./AllowlistedRules";
import RecentEmails from "./RecentEmails";
import { PersistedCollapse } from "../../PersistedSections";
import AutoPayAction from "./Actions/AutoPayAction";
import { getAutoPayStatus } from "../../../helpers/parsingHelper";
import { getLatestLoan, toWriteOff } from "../../../helpers/genericHelper";
import { PAYMENT_PRODUCT_TYPE } from "../../../constants/AppConstants";

const { Link, Text } = Typography;
const { Panel } = Collapse;

// A count shown next to a section title. Counts that point at a problem are red.
const CountTag = ({ count, label, problem = false }) => {
  if (count == null) return null;
  return <Tag color={problem && count > 0 ? "error" : "default"}>{label ? `${count} ${label}` : count}</Tag>;
};

const BlockedTag = ({ name, loading, status }) => {
  if (loading) return <Tag><LoadingOutlined /> {name}</Tag>;
  return status?.isBlocked
    ? <Tag color="error">{name} blocked</Tag>
    : <Tag color="success">{name} active</Tag>;
};

// A product's AutoPay status: a green "AutoPay Enabled" or red "AutoPay Disabled" box, with the day when enabled
const AutoPayLine = ({ on, day }) => (
  <>
    <Tag color={on ? "success" : "error"} style={{ marginTop: 4 }}>AutoPay {on ? "Enabled" : "Disabled"}</Tag>
    {on && day ? <span>Day {day}</span> : null}
  </>
);

// Statuses that end a product, shown in red on the product cards
const ENDED_STATUSES = ["DELINQUENT", "CLOSED", "CANCELLED", "CHARGED_OFF", "WRITE OFF"];

const ProductCard = ({ title, status, color = "default", detail }) => (
  <Card size="small" className="summary-card">
    <Text type="secondary"><small>{title}</small></Text>
    {/* A delinquent or ended product is always red, whatever the card's usual colour */}
    <div><Tag color={!status ? "default" : ENDED_STATUSES.includes(status) ? "error" : color}>{status || "None"}</Tag></div>
    {detail && <Text type="secondary"><small>{detail}</small></Text>}
  </Card>
);

function ProfileSummary({
  userSummary = {},
  loading,
  refreshList,
  firebaseInfo,
  creditCards,
  autoPayStatus,
  loanDetails
}) {
  const {
    reportScores,
    dwollaCustomer,
    registrationRisk,
    adminChanges,
    reportErrors,
    subscriptions,
    saveAndBuildCredit
  } = userSummary;

  const [data, setData] = useState({});
  const [tos, setTOS] = useState([]);
  const [revealedSSN, setRevealedSSN] = useState(null);

  const [dataLoading, setDataLoading] = useState(true);

  const [modalVisible, setModalVisible] = useState(false);

  const [modalLoading, showModalLoading] = useState(false);

  const [modalForForcePullVisible, setModalForForcePullVisible] = useState(false);
  const [modalForReenrollExperianVisible, setModalForReenrollExperianVisible] = useState(false);

  const [modalForAutopayVisible, setModalForAutopayVisible] = useState(false);
  const [modalForceAutoPayLoading, showModalForceAutoPayLoading] = useState(false);

  const [modalWhitelistUserSSNVisible, setModalWhitelistUserSSNVisible] = useState(false);
  const [modalUndoWhitelistUserSSNVisible, setModalUndoWhitelistUserSSNVisible] = useState(false);

  const [editAccountModalVisible, setEditAccountModalVisible] = useState(false);

  let { userId } = useParams();

  // null until loaded, so the section headers don't show a count of 0 while loading
  const [blockHistory, setBlockHistory] = useState(null);
  // Kept apart from an empty history, so a failed load is never shown as "no blocks"
  const [blockHistoryError, setBlockHistoryError] = useState(null);
  const [blockHistoryLoading, setBlockHistoryLoading] = useState(false);
  const [highRiskRules, setHighRiskRules] = useState(null);
  const [userBlockStatus, setUserBlockStatus] = useState(null);
  const [userBlockLoadingStatus, setUserBlockLoadingStatus] = useState(false);

  const [adverseActionNotices, setAdverseActionNotices] = useState(null);
  const [userFurnishingEvents, setUserFurnishingEvents] = useState([]);

  const reloadData = () => {
    if (refreshList) refreshList();
  }

  const getUserFurnishingEvents = async () => {
    const res = await API.getFurnishingEvents({ userId });
    setUserFurnishingEvents(res);
  }

  const getUserDetails = async (userId) => {
    try {
      setDataLoading(true);
      let res = await API.getUserSummary({ userId });
      setData(res);
    } catch (error) {
      message.error("Unable to fetch User Details");
    } finally {
      setDataLoading(false);
    }
  };

  const getTOS = async () => {
    try {
      let res = await API.getTOS({ userId });
      setTOS(res);
    } catch (error) {
      message.error("Unable to fetch User TOS");
    }
  };

  const getAdverseActionNotices = async (userId) => {
    try {
      const result = await API.getAdverseActionNotices(userId);
      setAdverseActionNotices(result);
    } catch (error) {
      message.error("Unable to fetch AANs")
    }
  }


  const revealSSN = async () => {
    try {
      const revealedSSNValue = await API.revealUserSSN({ userId })
      setRevealedSSN(revealedSSNValue);
    } catch (error) {
      message.error("Unable to fetch User SSN.");
    }
  }

  const enableBackupPayment = async () => {
    try {
      showModalLoading(true);
      await API.enableBackupPayment(userId);
      reloadData();
      message.success("Succesfully enabled the Backup Payment.");
      showModalLoading(false);
      setModalVisible(false);
    } catch (e) {
      message.error("Unable to enable the Backup Payment.");
      showModalLoading(false);
      setModalVisible(false);
    }
  };

  const forceLogout = async () => {
    try {
      showModalLoading(true);
      await API.revokeLogin({ userId });
      message.success("Succesfully logged out the user. The user will be logged out from all devices in 1 hour.");
      showModalLoading(false);
      setModalVisible(false);
      reloadData();
    } catch (e) {
      message.error("Unable to log out the user.");
      showModalLoading(false);
      setModalVisible(false);
    }
  };

  const forceAutoPay = async () => {
    try {
      showModalForceAutoPayLoading(true);
      await API.forceAutoPay(userId);
      reloadData();
      message.success("Succesfully enabled the Autopay.");
      showModalForceAutoPayLoading(false);
      setModalForAutopayVisible(false);
    } catch (e) {
      message.error("Unable to enable the Autopay.");
      showModalForceAutoPayLoading(false);
      setModalForAutopayVisible(false);
    }
  };

  const userStatusResponse = async (userId) => {
    try {
      setUserBlockLoadingStatus(true);
      let userStatus = await API.getUserBlockedStatus({ userId });
      setUserBlockStatus(userStatus);
      setUserBlockLoadingStatus(false);
    } catch (error) {
      message.error("Unable to fetch User Status");
    }
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const showEnableBackupPaymentModal = () => {
    setModalVisible(true);
  };

  const showForceAutoPayModal = () => {
    setModalForAutopayVisible(true);
  };

  const closeAutoPayModal = () => {
    setModalForAutopayVisible(false);
  };

  const loadBlockHistory = async () => {
    setBlockHistoryLoading(true);
    try {
      const res = await API.getBlockHistory(userId);
      setBlockHistory(res?.blockHistory ?? []);
      setBlockHistoryError(null);
    } catch (error) {
      setBlockHistoryError(error?.response?.data?.error_message || error?.message || "Request failed");
    } finally {
      setBlockHistoryLoading(false);
    }
  };

  const openEditAccountModal = () => {
    setEditAccountModalVisible(true);
  };

  const closeUpdateAccountModal = () => {
    setEditAccountModalVisible(false);
  };

  const handleShowForcePull = () => {
    setModalForForcePullVisible(true);
  }
  const closeForceReportPullModal = () => {
    setModalForForcePullVisible(false);
  }

  const forcefullyPullReport = async () => {
    try {
      showModalLoading(true);
      await API.forcefullyPullDocument(userId);
      message.success("Succesfully pulled the report.");
      showModalLoading(false);
      setModalForForcePullVisible(false);
      reloadData();
    } catch (e) {
      message.error("Unable to pull the report.");
      showModalLoading(false);
      setModalForForcePullVisible(false);
    }
  }

  const handleShowReenrollExperian = () => {
    setModalForReenrollExperianVisible(true);
  }
  const closeReenrollExperianModal = () => {
    setModalForReenrollExperianVisible(false);
  }

  const reenrollExperianUser = async () => {
    try {
      showModalLoading(true);
      await API.reenrollExperianUser(userId);
      message.success("Experian re-enrollment requested. Scheduled reports should resume; check back tomorrow, or use Force Pull if a report is needed now.");
      showModalLoading(false);
      setModalForReenrollExperianVisible(false);
    } catch (e) {
      message.error(e?.response?.data?.error_message || "Unable to request Experian re-enrollment.");
      showModalLoading(false);
      setModalForReenrollExperianVisible(false);
    }
  }

  const whitelistUserSSN = async () => {
    try {
      showModalLoading(true);
      await API.whitelistUserSSN(userId);
      message.success("Successfully whitelisted user's SSN");
      showModalLoading(false);
      closeWhitelistUserSSNModal();
      reloadData();
    } catch (e) {
      message.error(e?.response?.data?.error_message);
      showModalLoading(false);
      closeWhitelistUserSSNModal();
    }
  }

  const undoWhitelistUserSSN = async () => {
    try {
      showModalLoading(true);
      await API.undoWhitelistUserSSN(userId);
      message.success("Successfully removed whitelisted user's SSN");
      showModalLoading(false);
      closeUndoWhitelistUserSSNModal();
      reloadData();
    } catch (e) {
      message.error(e?.response?.data?.error_message);
      showModalLoading(false);
      closeUndoWhitelistUserSSNModal();
    }
  }


  const showWhitelistUserSSNModal = () => {
    setModalWhitelistUserSSNVisible(true);
  }

  const closeWhitelistUserSSNModal = () => {
    setModalWhitelistUserSSNVisible(false);
  }

  const showUndoWhitelistUserSSNModal = () => {
    setModalUndoWhitelistUserSSNVisible(true);
  }

  const closeUndoWhitelistUserSSNModal = () => {
    setModalUndoWhitelistUserSSNVisible(false);
  }

  const hasLoginProvider = (loginProvider) => {
    if (firebaseInfo?.providers) {
      return firebaseInfo.providers.some((provider) => provider.providerId === loginProvider);
    }
  }

  useEffect(() => {
    getTOS();
    userStatusResponse(userId);
    getUserDetails(userId);
    getAdverseActionNotices(userId);
    getUserFurnishingEvents();
    setBlockHistory(null);
    setBlockHistoryError(null);
    setHighRiskRules(null);
    // Loaded up front for the count and the "blocked since" date. A failure is shown in Block History with Retry.
    loadBlockHistory();
  }, [userId]);

  const {
    isUserBankrupt,
    silaUserStatus,
    avaUserStatus,
    bankruptcyFiledDate
  } = userBlockStatus || {};

  const subscription = subscriptions?.subscriptions?.find((sub) => sub.status === "ACTIVE") ?? subscriptions?.[0] ?? null;
  const creditCard = creditCards?.[0] ?? null;
  const savingsPlans = userSummary?.sbAdminResponse?.savingBuilders || [];
  // The most recent plan, not just the first one listed
  const savingsBuilder = [...savingsPlans].sort((a, b) => new Date(b?.sbLoanEntry?.createTs || 0) - new Date(a?.sbLoanEntry?.createTs || 0))[0] ?? null;
  // Status from the loan details, the same data the Save & Build page shows; the summary copy is only a fallback
  const latestSavingsLoan = getLatestLoan(loanDetails, savingsPlans.map((plan) => plan?.sbLoanEntry?.id));
  const savingsStatus = latestSavingsLoan?.loanStatus || savingsBuilder?.sbLoanEntry?.loanStatus;
  const savingsClosedOn = latestSavingsLoan?.loanClosureTs || savingsBuilder?.closureDate;
  const isAutoPayDisabled = autoPayStatus?.isDisabled == true;
  // Read the same way as each product's own page, so the two always agree
  const cardAutoPayStatus = getAutoPayStatus(autoPayStatus, PAYMENT_PRODUCT_TYPE.CREDIT_CARD);
  const cardAutoPay = { on: !(autoPayStatus?.isCreditCardAutoPayDisabled == true), day: cardAutoPayStatus?.autoPayDay };
  const savingsAutoPayStatus = getAutoPayStatus(autoPayStatus, PAYMENT_PRODUCT_TYPE.LOAN);
  const savingsAutoPay = { on: !savingsAutoPayStatus.isDisabled, day: savingsAutoPayStatus.autoPayDay };
  const latestScore = [...(reportScores || [])]
    .sort((a, b) => new Date(b.reportPullDate) - new Date(a.reportPullDate))[0];
  const lastBlock = [...(blockHistory || [])]
    .filter((entry) => entry.blockStatus === "BLOCKED")
    .sort((a, b) => new Date(b.actionInstant) - new Date(a.actionInstant))[0];
  const duplicateSsnEntries = data.duplicateSsnEntries || [];
  const hasDuplicateSsn = duplicateSsnEntries.length > 0;
  const riskLevel = registrationRisk?.length ? registrationRisk.map((_risk) => _risk.riskLevel).join(", ") : null;
  // Green for low, yellow for medium, red for high; with several levels the highest one sets the color
  const riskLevels = (registrationRisk || []).map((_risk) => String(_risk.riskLevel || '').toUpperCase());
  const riskColor = riskLevels.includes('HIGH') ? 'error'
    : riskLevels.includes('MEDIUM') ? 'warning'
    : riskLevels.includes('LOW') ? 'success'
    : 'default';
  const fullName = [data?.firstName, data?.lastName].filter(Boolean).join(" ");
  const initials = [data?.firstName, data?.lastName].map((name) => name?.[0] || "").join("").toUpperCase();

  const alerts = [
    isUserBankrupt && {
      type: "error",
      message: `The user has filed for bankruptcy since ${formatDate(bankruptcyFiledDate)} and cannot be unblocked.`
    },
    data.status === "DELETED" && {
      type: "error",
      message: `This user is deleted. Reason: ${data.deletedReason || "N/A"}`
    },
    avaUserStatus?.isBlocked && {
      type: "error",
      message: `Ava account is blocked${lastBlock ? ` since ${formatDate(lastBlock.actionInstant)}` : ""}${lastBlock?.blockCode ? ` (${lastBlock.blockCode})` : ""}. See Compliance Record for details.`
    },
    silaUserStatus?.isBlocked && {
      type: "error",
      message: "Sila account is blocked."
    },
    hasDuplicateSsn && {
      type: data.ssnWhitelisted ? "warning" : "error",
      message: `${duplicateSsnEntries.length} other ${duplicateSsnEntries.length === 1 ? "user shares" : "users share"} this SSN${data.ssnWhitelisted ? " (SSN whitelisted)" : ""}. See Duplicate Users below.`
    },
    highRiskRules?.length > 0 && {
      type: "error",
      message: `${highRiskRules.length} background check ${highRiskRules.length === 1 ? "issue" : "issues"}. See Background Check Issues below.`
    },
    isAutoPayDisabled && {
      type: "warning",
      message: "AutoPay is disabled."
    },
    reportErrors?.length > 0 && {
      type: "warning",
      message: `${reportErrors.length} credit report ${reportErrors.length === 1 ? "error" : "errors"}. See Credit Reporting.`
    }
  ].filter(Boolean);

  const paymentActions = isSupportLead() ? [
    <Button key="backup" onClick={showEnableBackupPaymentModal}>Enable Backup Payment</Button>,
    <AutoPayAction key="autopay" autoPayStatusEnabled={!isAutoPayDisabled} refreshList={refreshList} type={null} userId={userId} />,
    <Button key="run-autopay" onClick={showForceAutoPayModal}>Run AutoPay</Button>
  ] : [];

  const creditReportActions = isSupportAgent3() ? [
    <Button key="reenroll" onClick={handleShowReenrollExperian}>Re-Enroll User</Button>,
    <Button key="force-pull" onClick={handleShowForcePull}>Force Pull</Button>
  ] : [];

  const canChangeSsnWhitelist = isSupportLead() && data.status !== "REGISTERED_USER" && hasDuplicateSsn;
  const riskActions = [
    canChangeSsnWhitelist && <Button
      key="whitelist"
      disabled={data.ssnWhitelisted || duplicateSsnEntries.some((ssn) => ssn.isWhitelisted)}
      onClick={showWhitelistUserSSNModal}>
      Whitelist User's SSN
    </Button>,
    canChangeSsnWhitelist && data.ssnWhitelisted === true && <Button
      key="undo-whitelist"
      danger
      disabled={duplicateSsnEntries.some((ssn) => ssn.isWhitelisted)}
      onClick={showUndoWhitelistUserSSNModal}>
      Undo Whitelist User's SSN
    </Button>
  ];

  const accountActions = [
    isSupportAgent3() && <AuditProfile key="audit" reloadData={reloadData}
      userId={userId}
      modalLoading={modalLoading}
      showModalLoading={showModalLoading}
    />,
    <DocumentVerificationLink key="doc-link" reloadData={reloadData}
      userId={userId}
      modalLoading={modalLoading}
      showModalLoading={showModalLoading}
    />
  ];

  return (
    <>
      {dataLoading && loading ? (
        <LoaderComponent />
      ) : (
        <div className="summary-page">
          {/* Header */}
          <Card size="small" className="summary-header">
            <div className="summary-header-row">
              <div className="summary-avatar">{initials || "?"}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Space size={8} wrap>
                  <Text strong style={{ fontSize: 18 }}>{fullName || "N/A"}</Text>
                  <Tag color={data?.status === "REGISTERED_USER" ? "success" : "warning"}>{data?.status || "N/A"}</Tag>
                </Space>
                <div>
                  <Space size={4} wrap split={<Text type="secondary">·</Text>}>
                    <Text type="secondary" copyable={{ text: data?.userId, icon: <CopyOutlined /> }}>#{data?.userId}</Text>
                    <Text type="secondary" copyable={!!data?.email}>{data?.email || "N/A"}</Text>
                    <Text type="secondary">Joined {formatDate(data?.creationDate) || "N/A"}</Text>
                  </Space>
                </div>
                <Space size={4} wrap style={{ marginTop: 6 }}>
                  <BlockedTag name="Ava" loading={userBlockLoadingStatus} status={avaUserStatus} />
                  <Tag color={riskColor}>Risk: {riskLevel || "N/A"}</Tag>
                </Space>
              </div>
              <ProfileActions
                reloadData={reloadData}
                data={data}
                saveAndBuildCredit={saveAndBuildCredit}
                userId={userId}
                userBlockLoadingStatus={userBlockLoadingStatus}
                userBlockStatus={userBlockStatus}
                userStatusResponse={userStatusResponse}
                openEditAccountModal={openEditAccountModal}
                forceLogout={forceLogout}
                furnishingEvents={userFurnishingEvents}
                accountActions={accountActions}
                paymentActions={paymentActions}
                creditReportActions={creditReportActions}
                riskActions={riskActions}
              />
            </div>
          </Card>

          {/* Everything that needs attention, in one place */}
          {alerts.length > 0 &&
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              {alerts.map((alert) => <Alert key={alert.message} showIcon type={alert.type} message={alert.message} />)}
            </Space>
          }

          {/* Products */}
          <div className="summary-cards">
            <ProductCard title="Ava Membership" status={subscription?.status}
              color={subscription?.status === "ACTIVE" ? "success" : "error"} detail={subscription?.interval} />
            <ProductCard title="Credit Card" status={creditCard?.accountStatus}
              color={creditCard?.accountStatus === "ACTIVE" ? "success" : "processing"}
              detail={creditCard && <AutoPayLine on={cardAutoPay.on} day={cardAutoPay.day} />} />
            <ProductCard title="Savings Builder" status={toWriteOff(savingsStatus)} color="processing"
              detail={savingsClosedOn
                ? <>Closed {formatDate(savingsClosedOn)}{savingsPlans.length > 1 ? ` · ${savingsPlans.length} plans` : ""}</>
                : savingsStatus && <AutoPayLine on={savingsAutoPay.on} day={savingsAutoPay.day} />} />
            <Card size="small" className="summary-card">
              <Text type="secondary"><small>Credit Score</small></Text>
              <div><Text strong style={{ fontSize: 18 }}>{latestScore?.score ?? "N/A"}</Text></div>
              {latestScore && <Text type="secondary"><small>Pulled {formatDate(latestScore.reportPullDate)}</small></Text>}
            </Card>
          </div>

          {/* Details */}
          <Row gutter={[12, 12]}>
            <Col xs={24} lg={12} xl={9}>
              <Card size="small" title="Identity" className="summary-detail-card">
                <div className="summary-detail-grid">
                  <Data title="Full Name" value={fullName} copyable/>
                  <Data title="Date Of Birth" value={data?.dob}/>
                  <Data title="SSN">
                    {revealedSSN ?? data?.ssn}&nbsp;
                    {isSupportAgent3() && <Link onClick={revealSSN}><small>(reveal)</small></Link>}
                    {!isSupportManager() && isSupportLead() && <SSNMatch />}
                  </Data>
                  <Data title="SSN Whitelisted" value={data?.ssnWhitelisted === undefined ? null : String(data.ssnWhitelisted)}/>
                  <Data title="Email (verified)" value={data?.email} copyable/>
                  <PhoneNumberDisplay phoneNumber={data?.phoneNumber} />
                  <div style={{ gridColumn: "1 / -1" }}>
                    <Data title="Address">
                      {(data.addressLineOne || data.addressLineTwo || data.city || data.state || data.zip)
                        ? [data.addressLineOne, data.addressLineTwo, data.city, [data.state, data.zip].filter(Boolean).join(" ")]
                          .filter(Boolean).join(", ")
                        : null}
                    </Data>
                  </div>
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12} xl={8}>
              <Card size="small" title="Sign-up and Access" className="summary-detail-card">
                <div className="summary-detail-grid">
                  <Data title="Date (Created)" value={formatDate(data?.creationDate, true)}/>
                  <Data title="Date (Registered)" value={formatDate(data?.registrationTs, true)}/>
                  <Data title="Sign Up Platform" value={data?.signUpPlatform?.toUpperCase()}/>
                  <Data title="Device Type" value={data?.deviceType}/>
                  <Data title="Login Provider">
                    {hasLoginProvider(APPLE_PROVIDER) && <span><AppleFilled title="Apple" /> Apple </span>}
                    {hasLoginProvider(GOOGLE_PROVIDER) && <span><GoogleOutlined title="Google" style={{ color: "#4285F4" }} /> Google </span>}
                    {hasLoginProvider(EMAIL_PROVIDER) && <span><MailOutlined title="Email" /> Email</span>}
                  </Data>
                  <Data title="IP Address" value={data?.creationIp}/>
                </div>
              </Card>
            </Col>
            <Col xs={24} xl={7}>
              <Card size="small" className="summary-detail-card">
                <ProfileInfo subscriptions={subscriptions} all={userSummary} creditCards={creditCards} userId={userId} />
              </Card>
            </Col>
          </Row>

          {/* Problems that must not be missed, always open just above the grouped sections:
              background check issues (shown only when there are any), then duplicate users */}
          <HighRiskRules userId={userId} onRulesLoaded={setHighRiskRules} />
          {hasDuplicateSsn &&
            <Card className="duplicate-users-card">
              <h1 className="duplicate-users-banner">Duplicate Users ({duplicateSsnEntries.length})</h1>
              <DuplicateUsers duplicateUsers={duplicateSsnEntries.map(entry => ({ value: entry.userId, isWhitelisted: entry.isWhitelisted }))} />
            </Card>
          }

          {/* Grouped sections */}
          <PersistedCollapse name="profile-groups" className="summary-groups">
            <Panel
              header="Compliance Record"
              key="compliance"
              extra={<Space size={4}>
                <CountTag count={tos?.length ?? 0} label="terms accepted" />
                {blockHistoryError
                  ? <Tag color="warning">Block history couldn't load</Tag>
                  : <CountTag count={blockHistory?.length} label="blocks" />}
              </Space>}>
              <PersistedCollapse name="profile-compliance-sections" ghost>
                <Panel header={<>Terms of Service (acceptance time) <CountTag count={tos?.length ?? 0} /></>} key="tos">
                  <Space direction="vertical">
                    {tos && tos.length > 0 && tos.map((term) => (
                      <Data key={`${term.version}-${term.createTs}`}>
                        <strong>{formatLicenseVersion(term.version ?? '-')}</strong>
                        <br />
                        <small>- {formatDate(term.createTs, true)}</small>
                      </Data>
                    ))}
                  </Space>
                </Panel>
                <Panel header={<>Block History {blockHistoryError ? <Tag color="warning">Couldn't load</Tag> : <CountTag count={blockHistory?.length} />}</>} key="block-history">
                  {blockHistoryError
                    ? <Alert
                        type="warning"
                        showIcon
                        message="Block history couldn't be loaded"
                        description={blockHistoryError}
                        action={<Button size="small" onClick={loadBlockHistory} loading={blockHistoryLoading}>Retry</Button>} />
                    : blockHistory === null
                      ? <Text type="secondary">Loading block history…</Text>
                      : blockHistory.length > 0
                        ? <BlockHistory blockHistory={blockHistory} />
                        : <Row className="no-data-card">No Data Found</Row>}
                </Panel>
              </PersistedCollapse>
            </Panel>

            <Panel
              header="Risk and Verification"
              key="risk"
              forceRender
              extra={<Space size={4}>
                {hasDuplicateSsn && <CountTag count={duplicateSsnEntries.length} label="duplicates" problem />}
                <CountTag count={highRiskRules?.length} label="background issues" problem />
              </Space>}>
              <PersistedCollapse name="profile-risk-sections" ghost>
                <Panel header={<>Background Check Issues <CountTag count={highRiskRules?.length} problem /></>} key="high-risk">
                  {/* The issues themselves are in the red box above Compliance Record */}
                  {highRiskRules?.length > 0
                    ? <Text type="secondary">Shown in the red Background Check Issues box above Compliance Record.</Text>
                    : <Text type="secondary">No background check issues.</Text>}
                </Panel>
                <Panel header="Registration Details" key="registration">
                  <RegistrationDetails reloadData={reloadData}
                    registrationDetails={data.registrationDetails}
                    registrationDiagnostics={data.registrationDiagnostics}
                  />
                </Panel>
                {hasDuplicateSsn &&
                  <Panel header={<>Duplicate Users <CountTag count={duplicateSsnEntries.length} problem /></>} key="duplicates">
                    <DuplicateUsers duplicateUsers={duplicateSsnEntries.map(entry => ({ value: entry.userId, isWhitelisted: entry.isWhitelisted }))} />
                  </Panel>
                }
                <Panel header="Document Verifications" key="doc-verifications">
                  <DocumentVerifications userId={userId} />
                </Panel>
                <Panel header="Allowlisted Rules" key="allowlisted">
                  <AllowlistedRules userId={userId} />
                </Panel>
                {isSupportAgent3() &&
                  <Panel header="Upload Documents" key="sila-docs">
                    <SillaDocuments userId={userId} />
                  </Panel>
                }
              </PersistedCollapse>
            </Panel>

            <Panel
              header="Credit Reporting"
              key="credit"
              extra={<Space size={4}>
                <CountTag count={reportScores?.length ?? 0} label="scores" />
                <CountTag count={reportErrors?.length ?? 0} label="errors" problem />
                <CountTag count={adverseActionNotices?.length} label="notices" />
              </Space>}>
              <PersistedCollapse name="profile-credit-sections" ghost>
                <Panel header={<>User Score <CountTag count={reportScores?.length ?? 0} /></>} key="scores">
                  {reportScores && reportScores.length > 0 ? (
                    reportScores.map((_score) => {
                      return (
                        <Card
                          bordered={false}
                          className="criclebox tablespace mb-16"
                          key={_score.reportPullDate}
                        >
                          <Row
                            className="subscription-cancelled-card"
                            gutter={[30, 30]}
                            justify="space-between"
                          >
                            <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <h4>Report Id</h4>
                              <CustomText
                                dataSet={_score}
                                property1={"reportId"}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <h4>Score</h4>
                              <CustomText
                                dataSet={_score}
                                property1={"score"}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <h4>Report Pull Date</h4>
                              <CustomText
                                dataSet={_score}
                                property1={"reportPullDate"}
                                formatter={formatDate}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <h4>Last Ava Reported Date</h4>
                              <CustomText
                                dataSet={_score}
                                property1={"latestAvaTradelineReportedDate"}
                                formatter={formatDate}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <h4>Total Ava Accounts</h4>
                              <CustomText
                                dataSet={_score}
                                property1={"totalAvaAccounts"}
                              />
                            </Col>
                            { isDev() &&
                              <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                              <DownloadOutlined onClick={ () => downloadReport(userId, _score.reportId)} />
                              </Col>
                            }
                          </Row>
                        </Card>
                      );
                    })
                  ) : (
                    <Row className="no-data-card">No Data Found</Row>
                  )}
                </Panel>
                <Panel header={<>Report Errors <CountTag count={reportErrors?.length ?? 0} problem /></>} key="report-errors">
                  {reportErrors && reportErrors.length > 0 ? reportErrors.map((error) => {
                    return (
                      <Row className="subscription-cancelled-card" gutter={[30, 30]} key={error.error}>
                        <Col xs={24} sm={8} md={8} xl={8} xxl={8}>
                          <h4>Error</h4>
                          <CustomText dataSet={error} property1={'error'} />
                        </Col>
                        <Col xs={24} sm={8} md={8} xl={8} xxl={8}>
                          <h4>Detail</h4>
                          <CustomText dataSet={error} property1={'detail'} />
                        </Col>
                        <Col xs={24} sm={8} md={8} xl={8} xxl={8}>
                          <h4>Date</h4>
                          <CustomText dataSet={error}
                            property1={'reportPullTs'}
                            formatter={(val) => formatDate(val, true)} />
                        </Col>
                      </Row>
                    )
                  }) : (
                    <Row className="no-data-card">No Data Found</Row>
                  )}
                </Panel>
                <Panel header={<>Adverse Action Notices <CountTag count={adverseActionNotices?.length} /></>} key="aan">
                  {adverseActionNotices && adverseActionNotices.length > 0 ? adverseActionNotices.map((notice) => (
                    <Card bordered={false} className="criclebox tablespace mb-16" key={notice.id}>
                      <Row className="subscription-cancelled-card" gutter={[30, 30]}>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="ID" value={notice.id} />
                        </Col>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="Product" value={notice.product} />
                        </Col>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="Created Date" value={formatDate(notice.createTs)} />
                        </Col>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="Code" value={notice.adverseAction?.code} />
                        </Col>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="Reason" value={notice.adverseAction?.reason} />
                        </Col>
                        <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                          <Data title="Internal Cause" value={notice.adverseAction?.internalCause} />
                        </Col>
                      </Row>
                    </Card>
                  )) : (
                    <Row className="no-data-card">No Data Found</Row>
                  )}
                </Panel>
              </PersistedCollapse>
            </Panel>

            <Panel
              header="Audit"
              key="history"
              extra={<Space size={4}>
                <CountTag count={adminChanges?.length ?? 0} label="admin changes" />
              </Space>}>
              <PersistedCollapse name="profile-history-sections" ghost>
                <Panel header={<>Admin Changes <CountTag count={adminChanges?.length ?? 0} /></>} key="admin-changes">
                  {adminChanges && adminChanges.length > 0 ? (
                    <AdminChanges adminChanges={adminChanges} />
                  ) : (
                    <Row className="no-data-card">No Data Found</Row>
                  )}
                </Panel>
                <Panel header="Recent Emails" key="emails">
                  <RecentEmails userId={userId} />
                </Panel>
              </PersistedCollapse>
            </Panel>

            <Panel
              header="Partner Integration"
              key="integrations"
              extra={dwollaCustomer?.kycStatus && <Tag>Dwolla: {dwollaCustomer.kycStatus}</Tag>}>
              <Card bordered={false} size="small" title="Dwolla Customer">
                <Row
                  className="subscription-cancelled-card"
                  gutter={[30, 30]}
                >
                  <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                    <h4>Dwolla Customer ID</h4>
                    {dwollaCustomer?.dwollaCustomerId ? (
                      <Text copyable className="value-text">
                        {dwollaCustomer.dwollaCustomerId}
                      </Text>
                    ) : (
                      <CustomText
                        dataSet={dwollaCustomer}
                        property1={"dwollaCustomerId"}
                      />
                    )}
                  </Col>

                  <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                    <h4>KYC Status</h4>
                    <CustomText
                      dataSet={dwollaCustomer}
                      property1={"kycStatus"}
                    />
                  </Col>

                  <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                    <h4>Created</h4>
                    <CustomText
                      dataSet={dwollaCustomer}
                      property1={"createdTs"}
                      formatter={(ts) => formatDate(ts, true)}
                    />
                  </Col>
                </Row>
              </Card>
            </Panel>
          </PersistedCollapse>
        </div>
      )}
      <Modal
        title={"Confirmation"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        closable={true}
        visible={modalVisible}
        onOk={enableBackupPayment}
        onCancel={closeModal}
        className="confirm-modal"
      >
        <>
          <p>Are you sure you want to Enable Backup Payment?</p>
          {modalLoading && <LoaderComponent />}
        </>
      </Modal>


      <Modal
        title={"Confirmation"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        closable={true}
        visible={modalForAutopayVisible}
        onOk={forceAutoPay}
        onCancel={closeAutoPayModal}
        className="confirm-modal"
      >
        <>
          <p>Are you sure you want to Run AutoPay?</p>
          <p>This will run for both credit card and save & build credit.</p>

          {modalForceAutoPayLoading && <LoaderComponent />}
        </>
      </Modal>

      <Modal
        title={"Confirm force pull report!"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        okText={"Confirm"}
        onOk={forcefullyPullReport}
        closable={true}
        visible={modalForForcePullVisible}
        onCancel={closeForceReportPullModal}
        className="confirm-modal"
        cancelText="Close"
      >
        <>
          <p>This requests a fresh Experian credit report for the user right away. Please confirm only if it is necessary.</p>
          {modalLoading && <LoaderComponent />}
        </>
      </Modal>

      <Modal
        title={"Confirm Experian re-enrollment"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        okText={"Confirm"}
        onOk={reenrollExperianUser}
        closable={true}
        visible={modalForReenrollExperianVisible}
        onCancel={closeReenrollExperianModal}
        className="confirm-modal"
        cancelText="Close"
      >
        <>
          <p>
            Use this when Experian has dropped the user's enrollment and scheduled credit reports have stopped
            (the score in the app is stale). This deletes the user's current Experian subscriber and enrolls them
            again from scratch so that scheduled reports resume.
          </p>
          <p>
            <strong>This does not pull a report by itself.</strong> The next scheduled pull will show whether it
            worked, so check back tomorrow. If a fresh report is needed today, use Force Pull after re-enrolling.
          </p>
          {modalLoading && <LoaderComponent />}
        </>
      </Modal>

      <UpdateAccountModal
        editAccountModalVisible={editAccountModalVisible}
        closeUpdateAccountModal={closeUpdateAccountModal}
        userId={userId}
        userData={data}
        refetchUserData={reloadData}
      />

      <Modal
        title={"Confirmation"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        closable={true}
        visible={modalWhitelistUserSSNVisible}
        onOk={whitelistUserSSN}
        onCancel={closeWhitelistUserSSNModal}
        className="confirm-modal"
      >
        <>
          <p>Are you sure you want to whitelist this user's SSN?</p>
          {modalLoading && <LoaderComponent />}
        </>
      </Modal>

      <Modal
        title={"Confirmation"}
        centered
        mask={true}
        maskClosable={true}
        destroyOnClose={true}
        closable={true}
        visible={modalUndoWhitelistUserSSNVisible}
        onOk={undoWhitelistUserSSN}
        onCancel={closeUndoWhitelistUserSSNModal}
        className="confirm-modal"
      >
        <>
          <p>Are you sure you want to undo whitelisting of this user's SSN?</p>
          {modalLoading && <LoaderComponent />}
        </>
      </Modal>

    </>
  );
}


const downloadReport = async (userId, reportId) => {
  const rawReportString = await API.getReportFile({ userId, reportId});
  // Turn the string into a blob of type text/plain
  const file = new Blob([rawReportString], { type: "text/plain" });
  const url = window.URL.createObjectURL(file);
  // Now this opens up a new tab showing your JSON/XML text:
  window.open(url, "_blank");
}


export default ProfileSummary;
